import { PropsWithChildren, useEffect } from 'react';
import { clsx } from 'clsx';
import { LuArrowLeft, LuX } from 'react-icons/lu';

export type TopLevelSidePanelProps = PropsWithChildren & {
  title: string;
  show: boolean;
  onClose: () => void;
  onBack?: () => void;
};

const TopLevelSidePanel = ({ show, title, onClose, onBack, children }: TopLevelSidePanelProps) => {
  // Lock/unlock body scroll when panel is shown
  useEffect(() => {
    document.body.style.overflow = show ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [show]);

  return (
    <>
      {/* Dimmed backdrop (non-clickable) */}
      <div
        className={clsx(
          'fixed inset-0 z-50 bg-black transition-opacity duration-300',
          show ? 'bg-opacity-30 visible' : 'bg-opacity-0 pointer-events-none invisible'
        )}
        aria-hidden="true"
      />

      {/* Side panel container */}
      <div
        className={clsx(
          'fixed top-0 right-0 bottom-0 md:w-[27rem] w-full z-50 bg-white rounded-l-2xl shadow-xl',
          'transition-transform duration-300 ease-out flex flex-col overflow-hidden',
          show ? 'translate-x-0' : 'translate-x-full pointer-events-none'
        )}
        role="dialog"
        aria-modal="true"
      >
        {/* Header with optional back and close buttons */}
        <div className="flex items-center justify-between h-16 px-4 pt-4 pb-2 shadow-[0_2px_4px_-1px_rgba(0,0,0,0.1)]">
          <div className="flex items-center gap-2">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 rounded-full hover:bg-gray-100 focus:outline-none"
                aria-label="Back"
              >
                <LuArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
            )}
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 focus:outline-none"
            aria-label="Close"
          >
            <LuX className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
      </div>
    </>
  );
};

export default TopLevelSidePanel;
